import { useMemo, useState } from 'react';

import { Link, NotFound, Notification, Pagination, Typography, Wrapper } from '@/components';
import { useAuthToken, useAuthUser } from '@/hooks';
import { useTranslation } from 'react-i18next';
import { usePayouts } from '@/hooks/influencer';

import { Payout } from './Payout';
import styles from './Payouts.module.css';

interface MemoPayoutsProps {
  pageNumber: number;
  pageSize: number;
  countTotal: number;
  hasMore: true;
  payouts: Api.Influencer.Payout[];
}

export function Payouts() {
  const [currentPage, setCurrentPage] = useState(0);
  const user = useAuthUser();
  const token = useAuthToken();
  const { t } = useTranslation();

  const { data, isLoading, refetch } = usePayouts({
    currentPage,
    enabled: !!token && !!user?.user_id
  });

  const memoPayouts = useMemo(() => (data as MemoPayoutsProps) || {}, [data]);

  function prevPage() {
    return setCurrentPage((prevState) => prevState - 1);
  }

  function nextPage() {
    return setCurrentPage((prevState) => prevState + 1);
  }

  const maxPages = Math.ceil(memoPayouts.countTotal / memoPayouts.pageSize) || 0;

  const tax_country =
    (user?.missing_data &&
      Array.isArray(user.missing_data) &&
      !user?.missing_data?.includes('TAX_COUNTRY')) ||
    false;

  return (
    <Wrapper heading={t('payouts.heading')} isLoading={isLoading}>
      {!tax_country ? (
        <div className={styles.payoutsNoCountry}>
          <Typography sizes={['body']}>{t('payouts.noCountry')}</Typography>

          <div>
            <Link theme="cta" href="/connect/settings">
              {t('payouts.noCountryCta')}
            </Link>
          </div>
        </div>
      ) : (
        <Notification theme="info">
          <img className={styles.payoutsGigapay} src="/gigapay.png" alt={t('alt.gigapay')} />
          <p>{t('payout.gigapay1')}</p>
          <p>{t('payout.gigapay2')}</p>
          <p>{t('payout.gigapay3')}</p>
          <p>{t('payout.gigapay4')}</p>
        </Notification>
      )}

      {tax_country && (
        <>
          <div className={styles.payoutsPagination}>
            {maxPages > 1 && (
              <Pagination
                currentPage={currentPage}
                handleNextPage={nextPage}
                handlePrevPage={prevPage}
                maxPages={maxPages}
                hasMore={memoPayouts.hasMore}
              />
            )}
          </div>

          {memoPayouts.payouts?.length ? (
            memoPayouts.payouts.map((payout) => (
              <div className={styles.payoutsItem} key={`payout-${payout.collaboration_id}`}>
                <Payout payout={payout} onSuccess={refetch} />
              </div>
            ))
          ) : (
            <div className={styles.payoutsNotFound}>
              <NotFound text={t('payouts.notFound')} />
            </div>
          )}

          <div className={styles.payoutsPagination}>
            {maxPages > 1 && (
              <Pagination
                currentPage={currentPage}
                handleNextPage={nextPage}
                handlePrevPage={prevPage}
                maxPages={maxPages}
                hasMore={memoPayouts.hasMore}
              />
            )}
          </div>
        </>
      )}
    </Wrapper>
  );
}
